import React from 'react';
import { Modal, Button, Space, Card, Tag, Typography, Image as AntImage } from 'antd';
import { SwapOutlined, DeleteOutlined, CloseOutlined } from '@ant-design/icons';
import { formatCurrency } from '../../utils/format';
import env from '../../env';

const { Text, Title } = Typography;

/**
 * ComparisonModal Component
 * Displays a modal for comparing multiple lots side by side
 * 
 * @param {boolean} visible - Whether the modal is visible
 * @param {Function} onClose - Callback when modal is closed
 * @param {Array<number>} comparisonLots - Array of lot IDs to compare
 * @param {Array<Object>} lots - Array of all lot objects
 * @param {Function} onRemoveLot - Callback to remove a lot from comparison
 * @param {Function} onClearAll - Callback to clear all lots from comparison
 * @param {Object} currentUser - Current logged in user
 * @param {number} auctionOwnerId - ID of the auction owner
 */
const ComparisonModal = React.memo(({ 
    visible, 
    onClose, 
    comparisonLots, 
    lots, 
    onRemoveLot,
    onClearAll,
    currentUser,
    auctionOwnerId
}) => {
    const comparisonLotObjects = comparisonLots
        .map(lotId => lots.find(lot => lot.id === lotId))
        .filter(lot => lot !== undefined);

    if (comparisonLotObjects.length === 0) {
        return null;
    }

    const getLotImageUrl = (lot) => {
        if (lot.images && lot.images.length > 0) {
            const imagePath = lot.images[0].path;
            if (imagePath.startsWith('http')) {
                return imagePath;
            }
            return `${env.REACT_APP_API_URL}/storage/${imagePath}`;
        }
        return null;
    };

    const renderSpecifications = (lot) => {
        if (!lot.specifications || lot.specifications.length === 0) {
            return <Text type="secondary" style={{ fontSize: '12px' }}>No specifications</Text>;
        }
        return (
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {lot.specifications.map((specGroup, idx) => (
                    <div key={idx} style={{ marginBottom: '8px' }}>
                        <Text strong style={{ fontSize: '11px' }}>{specGroup.heading}:</Text>
                        <div style={{ marginLeft: '8px', fontSize: '11px', color: '#666' }}>
                            {specGroup.fields.map((field, fIdx) => {
                                const key = Object.keys(field)[0];
                                return (
                                    <div key={fIdx}>
                                        {key}: {field[key]}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Space>
                        <SwapOutlined />
                        <span>Lot Comparison ({comparisonLotObjects.length})</span>
                    </Space>
                    <Button 
                        type="text" 
                        danger 
                        size="small" 
                        icon={<DeleteOutlined />}
                        onClick={onClearAll}
                    >
                        Clear All
                    </Button>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={[
                <Button key="close" onClick={onClose}>
                    Close
                </Button>
            ]}
            width="95%"
            style={{ top: 20 }}
            bodyStyle={{ maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' }}
        >
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: window.innerWidth <= 768 
                    ? '1fr' 
                    : comparisonLotObjects.length <= 2 
                        ? `repeat(${comparisonLotObjects.length}, 1fr)`
                        : comparisonLotObjects.length === 3
                            ? 'repeat(3, 1fr)'
                            : 'repeat(4, 1fr)',
                gap: '16px',
                overflowX: 'auto'
            }}>
                {comparisonLotObjects.map((lot) => (
                    <Card
                        key={lot.id}
                        style={{
                            minWidth: '280px',
                            border: '2px solid #e0e0e0',
                            borderRadius: '8px'
                        }}
                        extra={
                            <Button
                                type="text"
                                size="small"
                                icon={<CloseOutlined />}
                                onClick={() => onRemoveLot(lot.id)}
                                danger
                            />
                        }
                    >
                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                            {/* Lot Number & Title */}
                            <div>
                                <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
                                    Lot #{lot.lot_number}
                                </Text>
                                <Title level={5} style={{ marginTop: '4px', marginBottom: '8px' }}>
                                    {lot.title}
                                </Title>
                            </div>

                            {/* Image */}
                            {getLotImageUrl(lot) ? (
                                <AntImage
                                    src={getLotImageUrl(lot)}
                                    alt={lot.title}
                                    style={{ 
                                        width: '100%', 
                                        height: '200px', 
                                        objectFit: 'cover',
                                        borderRadius: '4px'
                                    }}
                                    preview={true}
                                />
                            ) : (
                                <div style={{
                                    width: '100%',
                                    height: '200px',
                                    background: '#f5f5f5',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#999'
                                }}>
                                    No Image
                                </div>
                            )}

                            {/* Status */}
                            <div>
                                <Tag color={
                                    lot.status === 'active' ? 'green' :
                                    lot.status === 'closed' ? 'blue' :
                                    lot.status === 'cancelled' ? 'purple' : 'orange'
                                }>
                                    {lot.status.toUpperCase()}
                                </Tag>
                            </div>

                            {/* Prices */}
                            <div style={{ 
                                background: '#f9f9f9', 
                                padding: '12px', 
                                borderRadius: '4px' 
                            }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <Text type="secondary" style={{ fontSize: '11px' }}>Starting Price</Text>
                                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                                        {formatCurrency(lot.starting_price)}
                                    </div>
                                </div>
                                <div style={{ marginBottom: '8px' }}>
                                    <Text type="secondary" style={{ fontSize: '11px' }}>Current Price</Text>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}>
                                        {formatCurrency(lot.current_price || lot.starting_price)}
                                    </div>
                                </div>
                                {lot.reserve_price && currentUser?.id === auctionOwnerId && (
                                    <div>
                                        <Text type="secondary" style={{ fontSize: '11px' }}>Reserve Price</Text>
                                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#faad14' }}>
                                            {formatCurrency(lot.reserve_price)}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Description */}
                            <div data-tour="lot-description">
                                <Text type="secondary" style={{ fontSize: '12px', fontWeight: '500' }}>
                                    Description
                                </Text>
                                <div style={{ 
                                    marginTop: '4px', 
                                    fontSize: '12px', 
                                    color: '#666',
                                    maxHeight: '100px',
                                    overflowY: 'auto'
                                }}>
                                    {lot.description || 'No description available'}
                                </div>
                            </div>

                            {/* Specifications */}
                            <div>
                                <Text type="secondary" style={{ fontSize: '12px', fontWeight: '500' }}>
                                    Specifications
                                </Text>
                                <div style={{ marginTop: '4px' }}>
                                    {renderSpecifications(lot)}
                                </div>
                            </div>

                            {/* Additional Info */}
                            <div style={{ 
                                borderTop: '1px solid #f0f0f0', 
                                paddingTop: '12px',
                                fontSize: '11px',
                                color: '#999'
                            }}>
                                <div>Increment: {formatCurrency(lot.increment || lot.default_increment || 0)}</div>
                                {lot.quantity && <div>Quantity: {lot.quantity}</div>}
                            </div>
                        </Space>
                    </Card>
                ))}
            </div>
        </Modal>
    );
});

ComparisonModal.displayName = 'ComparisonModal';

export default ComparisonModal;

